import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

const bark = new THREE.MeshStandardMaterial({ name: "bark", color: "#4a2f24", roughness: 0.95, metalness: 0, flatShading: true });
const blossom = new THREE.MeshStandardMaterial({ name: "blossom", color: "#f7b8d0", roughness: 0.9, metalness: 0, flatShading: true });
const blossomDeep = new THREE.MeshStandardMaterial({ name: "blossom-deep", color: "#ef8fb4", roughness: 0.9, metalness: 0, flatShading: true });
const petals = new THREE.MeshStandardMaterial({ name: "petals", color: "#f9cadb", roughness: 1, metalness: 0, transparent: true, opacity: 0.85 });
const needles = new THREE.MeshStandardMaterial({ name: "needles", color: "#2f5d3a", roughness: 0.9, metalness: 0, flatShading: true });
const needlesLight = new THREE.MeshStandardMaterial({ name: "needles-light", color: "#3f7a4b", roughness: 0.9, metalness: 0, flatShading: true });
const oliveBark = new THREE.MeshStandardMaterial({ name: "olive-bark", color: "#6b5d4f", roughness: 1, metalness: 0, flatShading: true });
const oliveLeaves = new THREE.MeshStandardMaterial({ name: "olive-leaves", color: "#7d8f5f", roughness: 0.9, metalness: 0, flatShading: true });
const oliveLeavesSilver = new THREE.MeshStandardMaterial({ name: "olive-leaves-silver", color: "#a4b28a", roughness: 0.9, metalness: 0, flatShading: true });
const terracotta = new THREE.MeshStandardMaterial({ name: "terracotta", color: "#b8653f", roughness: 0.95, metalness: 0, flatShading: true });
const soil = new THREE.MeshStandardMaterial({ name: "soil", color: "#4a3626", roughness: 1, metalness: 0, flatShading: true });

function merged(geometries: THREE.BufferGeometry[], material: THREE.Material, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(mergeGeometries(geometries.map((geometry) => geometry.toNonIndexed())), material);
  mesh.name = name;
  return mesh;
}

/** Branche droite de `from` à `to`, s'affinant de `base` à `tip`. */
function limb(from: THREE.Vector3, to: THREE.Vector3, base: number, tip: number): THREE.BufferGeometry {
  const axis = new THREE.Vector3().subVectors(to, from);
  const geometry = new THREE.CylinderGeometry(tip, base, axis.length(), 6);
  geometry.translate(0, axis.length() / 2, 0);
  geometry.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), axis.normalize()));
  geometry.translate(from.x, from.y, from.z);
  return geometry;
}

function blob(radius: number, x: number, y: number, z: number, squash = 1): THREE.BufferGeometry {
  const geometry = new THREE.IcosahedronGeometry(radius, 1);
  geometry.scale(1, squash, 1);
  geometry.translate(x, y, z);
  return geometry;
}

const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

/** Cerisier en fleurs : tronc court qui s'ouvre en trois branches, nuage rose, pétales tombés au sol. Environ 4,5 m. */
export function buildSakura(): THREE.Group {
  const root = new THREE.Group();
  const fork = v(0.1, 1.9, 0);
  root.add(merged([
    limb(v(0, 0, 0), fork, 0.3, 0.2),
    limb(fork, v(1.3, 3, 0.4), 0.16, 0.07),
    limb(fork, v(-1.1, 3.1, 0.6), 0.15, 0.07),
    limb(fork, v(0.2, 3.3, -1.2), 0.15, 0.07),
  ], bark, "Trunk"));
  root.add(merged([
    blob(1.15, 1.3, 3.3, 0.4, 0.8), blob(1.05, -1.1, 3.4, 0.6, 0.8), blob(1.1, 0.2, 3.6, -1.2, 0.8), blob(1.2, 0.1, 4, 0, 0.75),
  ], blossom, "Blossom"));
  root.add(merged([
    blob(0.8, 0.9, 3.9, -0.6, 0.8), blob(0.75, -0.6, 3.9, -0.4, 0.8), blob(0.7, -0.3, 3.2, 1.3, 0.8), blob(0.7, 1.6, 2.9, -0.5, 0.8),
  ], blossomDeep, "BlossomDeep"));
  const carpet = new THREE.CircleGeometry(2.1, 18);
  carpet.rotateX(-Math.PI / 2);
  carpet.translate(0.1, 0.02, 0);
  root.add(merged([carpet], petals, "Petals"));
  return root;
}

/** Pin japonais : tronc qui ondule, branches horizontales portant des plateaux de feuillage étagés. Environ 4 m. */
export function buildJapanesePine(): THREE.Group {
  const root = new THREE.Group();
  const a = v(0.35, 1.2, 0.1), b = v(-0.25, 2.3, -0.1), c = v(0.15, 3.3, 0);
  root.add(merged([
    limb(v(0, 0, 0), a, 0.3, 0.24),
    limb(a, b, 0.24, 0.18),
    limb(b, c, 0.18, 0.12),
    limb(a, v(1.8, 1.7, 0.3), 0.11, 0.06),
    limb(b, v(-1.9, 2.6, 0.5), 0.1, 0.05),
    limb(b, v(0.4, 2.8, -1.6), 0.09, 0.05),
  ], bark, "Trunk"));
  root.add(merged([
    blob(1, 1.9, 1.85, 0.3, 0.35), blob(0.95, -1.95, 2.75, 0.5, 0.35), blob(1.15, 0.15, 3.6, 0, 0.4),
  ], needles, "Pads"));
  root.add(merged([
    blob(0.8, 0.45, 2.95, -1.65, 0.35), blob(0.6, 1.2, 2, -0.3, 0.35), blob(0.65, -0.6, 4, 0.3, 0.35),
  ], needlesLight, "PadsLight"));
  return root;
}

/** Olivier : tronc court, noueux, qui se sépare en branches penchées sous un feuillage gris argenté en touffes. Environ 4,5 m. */
export function buildOlive(): THREE.Group {
  const root = new THREE.Group();
  const knot = v(0.15, 0.9, 0.05), left = v(-0.35, 1.6, 0.2), right = v(0.55, 1.5, -0.15);
  root.add(merged([
    limb(v(0, 0, 0), knot, 0.42, 0.32),
    limb(v(0.12, 0, -0.1), v(0.3, 0.8, -0.05), 0.24, 0.16),
    limb(knot, left, 0.24, 0.17),
    limb(knot, right, 0.22, 0.15),
    limb(left, v(-1.3, 2.7, 0.6), 0.16, 0.07),
    limb(left, v(-0.2, 3, 1.1), 0.13, 0.06),
    limb(right, v(1.5, 2.6, -0.4), 0.15, 0.07),
    limb(right, v(0.6, 3.1, -1.1), 0.12, 0.06),
  ], oliveBark, "Trunk"));
  root.add(merged([
    blob(1.05, -1.3, 3, 0.6, 0.6), blob(0.95, 1.5, 2.9, -0.4, 0.6), blob(1.1, 0.1, 3.7, 0, 0.55), blob(0.85, -0.2, 3.2, 1.2, 0.6),
  ], oliveLeaves, "Leaves"));
  root.add(merged([
    blob(0.75, 0.7, 3.5, -1.1, 0.6), blob(0.7, -0.9, 3.8, -0.3, 0.6), blob(0.65, 1.1, 3.4, 0.7, 0.6), blob(0.6, -1.8, 2.7, -0.2, 0.6),
  ], oliveLeavesSilver, "LeavesSilver"));
  return root;
}

/** Olivier en pot : jarre de terre cuite, tronc fin et houppier arrondi, de quoi border une terrasse ou un stand. Environ 2,2 m. */
export function buildPottedOlive(): THREE.Group {
  const root = new THREE.Group();
  const pot = new THREE.CylinderGeometry(0.42, 0.3, 0.75, 10);
  pot.translate(0, 0.375, 0);
  const rim = new THREE.CylinderGeometry(0.48, 0.48, 0.1, 10);
  rim.translate(0, 0.78, 0);
  root.add(merged([pot, rim], terracotta, "Pot"));
  const earth = new THREE.CylinderGeometry(0.4, 0.4, 0.04, 10);
  earth.translate(0, 0.8, 0);
  root.add(merged([earth], soil, "Soil"));
  const fork = v(0.06, 1.45, 0);
  root.add(merged([
    limb(v(0, 0.8, 0), fork, 0.08, 0.06),
    limb(fork, v(-0.3, 1.75, 0.15), 0.05, 0.03),
    limb(fork, v(0.3, 1.8, -0.1), 0.05, 0.03),
  ], oliveBark, "Trunk"));
  root.add(merged([
    blob(0.42, -0.25, 1.85, 0.1, 0.8), blob(0.4, 0.3, 1.9, -0.1, 0.8), blob(0.38, 0, 2.05, 0.25, 0.8),
  ], oliveLeaves, "Leaves"));
  root.add(merged([
    blob(0.3, 0.05, 1.95, -0.3, 0.8), blob(0.28, -0.35, 2, -0.15, 0.8), blob(0.26, 0.35, 2.05, 0.25, 0.8),
  ], oliveLeavesSilver, "LeavesSilver"));
  return root;
}
